import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsersGear,
  faPlus,
  faSpinner,
  faEdit,
  faTrash,
  faTimes,
  faCheck,
  faUserPlus,
  faUserMinus,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import teamService from '../services/teamService';
import { useTheme } from '../contexts/ThemeContext';
import PageHeader from '../components/common/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import TenantSelector from '../components/common/TenantSelector';

const TeamsManagement = () => {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'admin';
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState('');
  const { darkMode } = useTheme();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'red',
    color: '#EF4444',
    tenant: null
  });

  const typeColors = {
    red: '#EF4444',
    blue: '#3B82F6',
    purple: '#8B5CF6',
    mixed: '#6B7280'
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const data = await teamService.getTeams();
      setTeams(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      await teamService.createTeam(formData);
      toast.success('Team created successfully');
      setShowCreateModal(false);
      setFormData({ name: '', description: '', type: 'red', color: '#EF4444', tenant: null });
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create team');
    }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    try {
      await teamService.updateTeam(editingTeam._id, formData);
      toast.success('Team updated successfully');
      setEditingTeam(null);
      setFormData({ name: '', description: '', type: 'red', color: '#EF4444', tenant: null });
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update team');
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    try {
      await teamService.deleteTeam(teamId);
      toast.success('Team deleted successfully');
      fetchTeams();
    } catch (error) {
      toast.error('Failed to delete team');
    }
  };

  const handleExpandTeam = async (team) => {
    if (expandedTeam === team._id) {
      setExpandedTeam(null);
      return;
    }
    setExpandedTeam(team._id);
    try {
      const users = await teamService.getAvailableUsers(team._id);
      setAvailableUsers(users);
    } catch (error) {
      console.error('Error fetching available users:', error);
    }
  };

  const handleAddMember = async (teamId) => {
    if (!selectedUserToAdd) return;
    try {
      await teamService.addMember(teamId, selectedUserToAdd, 'member');
      toast.success('Member added to team');
      setSelectedUserToAdd('');
      fetchTeams();
      const users = await teamService.getAvailableUsers(teamId);
      setAvailableUsers(users);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (teamId, memberId) => {
    if (!window.confirm('Remove this member from the team?')) return;
    try {
      await teamService.removeMember(teamId, memberId);
      toast.success('Member removed from team');
      fetchTeams();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const openEditModal = (team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      description: team.description || '',
      type: team.type,
      color: team.color || typeColors[team.type],
      tenant: team.tenant?._id || team.tenant || null
    });
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingTeam(null);
    setFormData({ name: '', description: '', type: 'red', color: '#EF4444', tenant: null });
  };

  const isModalOpen = showCreateModal || !!editingTeam;
  const isEdit = !!editingTeam;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Teams Management"
        subtitle="Manage team groups and their members"
      >
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <FontAwesomeIcon icon={faPlus} />
          <span>New Team</span>
        </button>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Teams</p>
          <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{teams.length}</p>
        </div>
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 bg-red-500 rounded-full"></span>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Red Teams</p>
          </div>
          <p className="text-2xl font-bold text-red-500">{teams.filter(t => t.type === 'red').length}</p>
        </div>
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Blue Teams</p>
          </div>
          <p className="text-2xl font-bold text-blue-500">{teams.filter(t => t.type === 'blue').length}</p>
        </div>
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 bg-purple-500 rounded-full"></span>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Purple Teams</p>
          </div>
          <p className="text-2xl font-bold text-purple-500">{teams.filter(t => t.type === 'purple').length}</p>
        </div>
      </div>

      {/* Teams List */}
      <div className="space-y-4">
        {teams.map((team) => (
          <div
            key={team._id}
            className={`rounded-lg shadow overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
          >
            {/* Team Header */}
            <div
              className={`p-4 cursor-pointer ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
              onClick={() => handleExpandTeam(team)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: team.color }}
                  >
                    {team.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {team.name}
                    </h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        team.type === 'red' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                        team.type === 'blue' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                        team.type === 'purple' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {team.type.toUpperCase()}
                      </span>
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {team.members?.length || 0} members
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExpandTeam(team);
                    }}
                    className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-1"
                    title="Manage members"
                  >
                    <FontAwesomeIcon icon={faUserPlus} />
                    <span>Members</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(team);
                    }}
                    className={`p-2 rounded ${darkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTeam(team._id);
                    }}
                    className={`p-2 rounded ${darkMode ? 'hover:bg-gray-600 text-red-400' : 'hover:bg-gray-200 text-red-500'}`}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                  <FontAwesomeIcon
                    icon={expandedTeam === team._id ? faChevronUp : faChevronDown}
                    className={darkMode ? 'text-gray-400' : 'text-gray-500'}
                  />
                </div>
              </div>
            </div>

            {/* Expanded Team Members */}
            {expandedTeam === team._id && (
              <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} p-4`}>
                {team.description && (
                  <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {team.description}
                  </p>
                )}

                {/* Add Member */}
                <div className="flex items-center space-x-2 mb-4">
                  <select
                    value={selectedUserToAdd}
                    onChange={(e) => setSelectedUserToAdd(e.target.value)}
                    className={`flex-1 px-3 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Select user to add...</option>
                    {availableUsers.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.displayName} ({user.email})
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleAddMember(team._id)}
                    disabled={!selectedUserToAdd}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faUserPlus} />
                  </button>
                </div>

                {/* Members List */}
                <div className="space-y-2">
                  <h4 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Team Members
                  </h4>
                  {team.members?.length > 0 ? (
                    <div className="space-y-2">
                      {team.members.map((member) => (
                        <div
                          key={member._id}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold ${
                              member.role === 'lead' ? 'bg-yellow-500' : 'bg-gray-500'
                            }`}>
                              {member.user?.displayName?.charAt(0).toUpperCase() || '?'}
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {member.user?.displayName || 'Unknown User'}
                              </p>
                              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {member.user?.email} • {member.role}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveMember(team._id, member._id)}
                            className={`p-2 rounded ${darkMode ? 'hover:bg-gray-600 text-red-400' : 'hover:bg-gray-200 text-red-500'}`}
                            title="Remove from team"
                          >
                            <FontAwesomeIcon icon={faUserMinus} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      No members in this team yet
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {teams.length === 0 && (
          <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <FontAwesomeIcon icon={faUsersGear} className="text-4xl mb-3" />
            <p>No teams created yet</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-3 text-blue-500 hover:text-blue-600"
            >
              Create your first team
            </button>
          </div>
        )}
      </div>

      {/* Team Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 w-full max-w-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {isEdit ? 'Edit Team' : 'Create New Team'}
              </h2>
              <button
                onClick={closeModal}
                className={darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={isEdit ? handleUpdateTeam : handleCreateTeam}>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Team Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className={`w-full px-3 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-blue-500`}
                    placeholder="e.g., Alpha Strike Team"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-blue-500`}
                    placeholder="Team description..."
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Team Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({
                      ...formData,
                      type: e.target.value,
                      color: typeColors[e.target.value]
                    })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="red">Red Team</option>
                    <option value="blue">Blue Team</option>
                    <option value="purple">Purple Team</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Team Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {formData.color}
                    </span>
                  </div>
                </div>

                <TenantSelector
                  value={formData.tenant}
                  onChange={(val) => setFormData({ ...formData, tenant: val })}
                />
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className={`px-4 py-2 rounded-lg ${
                    darkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {isEdit ? 'Update Team' : 'Create Team'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamsManagement;
